import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, X as XIcon, Lock, ListChecks,
  Stethoscope, Sparkles, ShieldCheck, FileSignature,
} from "lucide-react";

/**
 * WizardGuidedTour — interactive 6-step walkthrough for first-time RNs.
 *
 * Cross-page tour: each step references both a `route` (wizard sub-step
 * URL) AND a `target` selector. When advancing, if the next step's
 * route differs from the current one, we navigate first, then poll for
 * the target element to mount before measuring its rect.
 *
 * Steps cover the highest-leverage features RNs need to know about:
 *   1. Agency block on Step 1 (locked-from-Avida — they can't edit this)
 *   2. ADL scoring on Step 2 (one-tap PCST chips, DHS rules baked in)
 *   3. MAHC-10 auto-calc on Step 7 (fall-risk scoring auto-generated)
 *   4. AI compliance review on Step 8 (Claude Sonnet 4.5 catches errors
 *      before payer does)
 *   5. Verification checklist on Step 9 (13-item RN gate)
 *   6. Generate button on Step 9 (the production gate; explains the
 *      triple-gate protection)
 *
 * Persists `wi-pa-wizard-tour-seen-v1` in localStorage so the launcher
 * pill in the wizard header pulses for first-time RNs only.
 */

const STEPS = [
  {
    route: "/wizard/1",
    target: '[data-testid="step1-section-agency"]',
    icon: Lock,
    title: "1. Agency block — locked from Avida",
    body: (
      <>
        <p>
          Provider, NPI, and physician details come from Avida Care.
          You can&apos;t edit them here — that prevents fat-finger errors
          on the PA packet.
        </p>
        <p className="mt-2">
          The yellow <b>Locked</b> pill confirms each block is
          authoritative. If something looks wrong, fix it on the Avida
          side, not here.
        </p>
      </>
    ),
    placement: "bottom",
  },
  {
    route: "/wizard/2",
    target: '[data-testid="adl-row-bathing"]',
    icon: ListChecks,
    title: "2. ADL scoring — one-tap chips",
    body: (
      <>
        <p>
          Each ADL row has independence-level chips (Independent /
          Setup / Supervision / Limited / Extensive / Total). Tap the
          right one — no scrolling through nested menus.
        </p>
        <p className="mt-2">
          DHS rules run silently behind every selection. If Bathing is
          Total but Hygiene is Independent, you&apos;ll see a yellow
          inconsistency banner appear instantly.
        </p>
      </>
    ),
    placement: "right",
  },
  {
    route: "/wizard/7",
    target: '[data-testid="nsa-mahc-auto"]',
    icon: Sparkles,
    title: "3. MAHC-10 auto-calc",
    body: (
      <>
        <p>
          Tap <b>Auto-calc from this visit&apos;s data</b> and the
          MAHC-10 fall-risk score is computed from history, vision,
          medications, and ADLs that you already entered.
        </p>
        <p className="mt-2">
          Saves ~3 minutes per visit and removes the 8% scoring-error
          rate we used to see in manual MAHC-10 calculations.
        </p>
      </>
    ),
    placement: "bottom",
  },
  {
    route: "/wizard/8",
    target: '[data-testid="ai-review-trigger"]',
    icon: ShieldCheck,
    title: "4. AI compliance review",
    body: (
      <>
        <p>
          One click runs Claude Sonnet 4.5 over the full intake
          payload checking every Wisconsin DHS rule, signature
          requirement, and certification-period constraint.
        </p>
        <p className="mt-2">
          Errors get flagged with line-level fix instructions. Catches
          payer-rejection issues at the visit, not 6 weeks later.
        </p>
      </>
    ),
    placement: "left",
  },
  {
    route: "/wizard/9",
    target: '[data-testid="gate-checklist"]',
    icon: ListChecks,
    title: "5. Verification gates",
    body: (
      <>
        <p>
          5 gates protect the <b>Generate</b> button: DHS rules pass,
          AI review pass, 13-item checklist complete, RN signature
          captured, client signature captured.
        </p>
        <p className="mt-2">
          Each gate turns from gray → green as you satisfy it. Not all
          green = you can&apos;t generate. This is the safety net.
        </p>
      </>
    ),
    placement: "right",
  },
  {
    route: "/wizard/9",
    target: '[data-testid="generate-pdfs-btn"]',
    icon: FileSignature,
    title: "6. Generate the PA packet",
    body: (
      <>
        <p>
          When all 5 gates are green, tap Generate. We render 5 signed
          PDFs (F-11133, F-11134, F-11136, CMS-485, Care Plan), POST
          the bundle to Avida Care, and route you to the Download
          Center.
        </p>
        <p className="mt-2">
          Average time from button-press to printable packet: ~9
          seconds. You&apos;re done.
        </p>
      </>
    ),
    placement: "top",
  },
];


function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

const TOUR_SEEN_KEY = "wi-pa-wizard-tour-seen-v1";

export function markTourSeen() {
  try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch {}
}

export function isTourSeen() {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(TOUR_SEEN_KEY) === "1";
}

// Telemetry: fire-and-forget POST to /api/wizard/tour-event so coordinators
// can see who actually completes the tour vs. clicks the launcher and bails.
// Never throws — telemetry must never crash a wizard session.
function logTourEvent(eventType, stepIndex, stepTarget) {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    if (!apiBase) return;
    let rnEmail = "";
    try {
      const raw = window.sessionStorage.getItem("wi-pa-parent-claims-v1");
      if (raw) {
        const claims = JSON.parse(raw);
        rnEmail = (claims && claims.email) || "";
      }
    } catch { /* claims unavailable — fall through */ }
    const body = JSON.stringify({
      event_type: eventType,
      step_index: stepIndex,
      step_target: stepTarget || "",
      rn_email: rnEmail,
    });
    // keepalive lets the request survive the unmount that follows Finish/ESC
    fetch(`${apiBase}/api/wizard/tour-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}


export default function WizardGuidedTour({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);
  // Track whether the most recent close was via the Finish button so the
  // shared onClose() path can distinguish "completed" vs. "skipped" without
  // needing a separate handler everywhere ESC/X/backdrop fire.
  const finishingRef = useRef(false);
  const startedAtIndexRef = useRef(0);

  const step = STEPS[index];

  const advance = useCallback((direction) => {
    setIndex((i) => clamp(i + direction, 0, STEPS.length - 1));
  }, []);

  // Wrap onClose so any non-Finish close path logs a "tour_skipped" with
  // the index the RN bailed on. Finish button sets finishingRef before
  // calling close, so it logs "tour_completed" instead.
  const handleClose = useCallback(() => {
    if (finishingRef.current) {
      logTourEvent("tour_completed", STEPS.length - 1, STEPS[STEPS.length - 1].target);
    } else {
      logTourEvent("tour_skipped", index, step?.target || "");
    }
    finishingRef.current = false;
    onClose();
  }, [index, step, onClose]);

  // Fire a "tour_started" event the first time `open` flips to true in
  // this mount cycle. Reset state when the tour closes so a re-open
  // starts fresh from step 0.
  useEffect(() => {
    if (open) {
      logTourEvent("tour_started", 0, STEPS[0].target);
      startedAtIndexRef.current = 0;
      setIndex(0);
    }
  }, [open]);

  // Whenever the active step changes, navigate (if needed) + poll for
  // the target element. The wizard sub-step routes mount lazily so the
  // target element can take a moment to appear.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let pollHandle = null;

    if (location.pathname !== step.route) {
      navigate(step.route);
    }

    const findAndMeasure = (attempts = 20) => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (!el) {
        if (attempts > 0) {
          pollHandle = setTimeout(() => findAndMeasure(attempts - 1), 200);
        } else {
          // Give up — render tooltip in the center with no spotlight
          setRect(null);
        }
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top, left: r.left, width: r.width, height: r.height,
          bottom: r.bottom, right: r.right,
        });
      }, 350);
    };

    // Wait one tick to give the new route a chance to render
    setTimeout(findAndMeasure, 50);

    const onResize = () => findAndMeasure(0);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      cancelled = true;
      if (pollHandle) clearTimeout(pollHandle);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
  }, [open, step.route, step.target, location.pathname, navigate]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (index === STEPS.length - 1) {
          finishingRef.current = true;
          handleClose();
        } else {
          advance(1);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        advance(-1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, advance, handleClose]);

  if (!open) return null;

  // Tooltip placement (right / left / top / bottom) with viewport-fit
  const tooltipStyle = (() => {
    if (!rect) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    const TOOLTIP_W = 360;
    const TOOLTIP_H = 280;  // approximate; varies per step
    const PAD = 16;
    const placement = step.placement;
    let top, left;
    if (placement === "right" && rect.right + TOOLTIP_W + PAD < window.innerWidth) {
      top = rect.top;
      left = rect.right + PAD;
    } else if (placement === "left" && rect.left - TOOLTIP_W - PAD > 0) {
      top = rect.top;
      left = rect.left - TOOLTIP_W - PAD;
    } else if (placement === "top" && rect.top > TOOLTIP_H + PAD) {
      top = rect.top - TOOLTIP_H - PAD;
      left = rect.left;
    } else {
      // default: bottom
      top = rect.bottom + PAD;
      left = rect.left;
    }
    // Clamp to viewport on both axes so tooltip is never off-screen
    top = clamp(top, PAD, window.innerHeight - TOOLTIP_H - PAD);
    left = clamp(left, PAD, window.innerWidth - TOOLTIP_W - PAD);
    return { top: top + "px", left: left + "px" };
  })();

  const StepIcon = step.icon;

  return (
    <div
      className="fixed inset-0 z-50 pointer-events-none"
      data-testid="wizard-tour-overlay"
    >
      {rect ? (
        <>
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{ top: 0, left: 0, right: 0, height: rect.top - 8 + "px" }}
            onClick={handleClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.top - 8 + "px", left: 0,
              width: rect.left - 8 + "px",
              height: rect.height + 16 + "px",
            }}
            onClick={handleClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.top - 8 + "px",
              left: rect.right + 8 + "px", right: 0,
              height: rect.height + 16 + "px",
            }}
            onClick={handleClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.bottom + 8 + "px",
              left: 0, right: 0, bottom: 0,
            }}
            onClick={handleClose}
          />
          <div
            className="fixed pointer-events-none rounded-xl ring-4 ring-[#9333EA] transition-all duration-300"
            style={{
              top: rect.top - 4 + "px",
              left: rect.left - 4 + "px",
              width: rect.width + 8 + "px",
              height: rect.height + 8 + "px",
            }}
          />
        </>
      ) : (
        <div
          className="fixed inset-0 bg-black/70 pointer-events-auto"
          onClick={handleClose}
        />
      )}

      <div
        ref={tooltipRef}
        className="fixed bg-white rounded-xl shadow-xl p-4 pointer-events-auto transition-all duration-300"
        style={{ ...tooltipStyle, width: "360px", maxWidth: "92vw" }}
        data-testid="wizard-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9333EA] font-bold flex items-center gap-1.5">
              {StepIcon && <StepIcon className="w-3 h-3" />}
              Wizard tour · {index + 1} of {STEPS.length}
            </div>
            <h3 className="font-display text-[16px] font-bold text-[#0B1E3A] mt-0.5">
              {step.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-[#3A5275] hover:text-[#0B1E3A] -mt-1 -mr-1 p-1 rounded-md"
            data-testid="wizard-tour-close"
            aria-label="Close tour"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[13px] text-[#3A5275] leading-relaxed">
          {step.body}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`block h-1.5 rounded-full transition-all ${
                  i === index ? "w-6 bg-[#9333EA]" : "w-1.5 bg-[#E5E7EB]"
                }`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => advance(-1)}
              disabled={index === 0}
              className="px-2.5 py-1 rounded-md text-[12px] font-semibold text-[#3A5275] disabled:opacity-40 hover:bg-[#F3F4F6]"
              data-testid="wizard-tour-prev"
            >
              <ChevronLeft className="w-4 h-4 inline" />
            </button>
            {index === STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => { finishingRef.current = true; handleClose(); }}
                className="px-3 py-1 rounded-md text-[12px] font-semibold bg-[#9333EA] text-white hover:bg-[#7E22CE]"
                data-testid="wizard-tour-finish"
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                onClick={() => advance(1)}
                className="px-3 py-1 rounded-md text-[12px] font-semibold bg-[#9333EA] text-white hover:bg-[#7E22CE]"
                data-testid="wizard-tour-next"
              >
                Next <ChevronRight className="w-4 h-4 inline" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
